import { useEffect, useMemo, useState } from 'react';
import type { SyntheticEvent } from 'react';
import { fetchOembedThumbnail, isPlaceholderThumbnail, parseVideoUrl } from './videos';

/**
 * Resolves the best available poster frame for a video and hands back the `img`
 * handlers that keep it correct:
 *
 *  - an explicit `poster` always wins;
 *  - YouTube starts on `maxresdefault` and steps down to `hqdefault` when that
 *    frame doesn't exist (which YouTube signals with a 120px grey image, not a 404);
 *  - Loom and Vimeo are fetched from their oEmbed endpoints, since the URL
 *    isn't derivable.
 *
 * `src` is null when nothing resolved — render the gradient placeholder then.
 */
export function useVideoThumbnail(url: string, poster?: string) {
    const video = useMemo(() => parseVideoUrl(url), [url]);
    const [src, setSrc] = useState<string | null>(poster ?? video?.thumbnailUrl ?? null);

    useEffect(() => {
        setSrc(poster ?? video?.thumbnailUrl ?? null);
        const oembedUrl = video?.oembedUrl;
        if (poster || !oembedUrl) return;

        const controller = new AbortController();
        fetchOembedThumbnail(oembedUrl, controller.signal).then((resolved) => {
            if (resolved) setSrc(resolved);
        });
        return () => controller.abort();
    }, [poster, video]);

    return {
        src,
        /** The URL 404'd — step down, then give up. */
        onError: () =>
            setSrc((current) =>
                current === video?.thumbnailUrl ? video?.fallbackThumbnailUrl ?? null : null
            ),
        /** It loaded, but YouTube served its grey "no max-res frame" placeholder. */
        onLoad: (e: SyntheticEvent<HTMLImageElement>) => {
            if (src === video?.thumbnailUrl && isPlaceholderThumbnail(e.currentTarget)) {
                setSrc(video?.fallbackThumbnailUrl ?? null);
            }
        },
    };
}
